import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Control, Field, Tab, Title, Button, Icon } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import { useApolloClient } from "react-apollo";
import {
  WORK_ORDER_COLUMNS,
  generateApprovedWorkOrderColumns
} from "../WorkOrders/columns";
import {
  ALL_WORK_ORDERS_VIEW_BY_PAGE_QUERY,
  UPDATE_WORK_ORDER_MUTATION
} from "../../../graphql/WorkOrders/WorkOrders.graphql";
import {
  ALL_VEHICLE_INSPECTIONS_QUERY,
  CREATE_VEHICLE_INSPECTION_MUTATION
} from "../../../graphql/Vehicles/Vehicles.graphql";
import { SINGLE_EMPLOYEE_QUERY } from "../../../graphql/Employees/Employees.graphql";

import DataTable from "../../../components/DataTable";
import {
  getDaysDiffBetweenDates,
  getUTCFormattedDate,
  runPromisesInSeries
} from "../../../utils";
import { useSystemCodes, useCurrentUser } from "../../../utils/hooks";
import { SINGLE_ITEM_CATEGORY_QUERY } from "../../../graphql/ItemCategories/ItemCategories.graphql";

import { ALL_WORK_ORDER_PARTS_QUERY } from "../../../graphql/WorkOrders/WorkOrderParts.graphql";

const WOProcessingPage = () => {
  const SystemCodes = useSystemCodes({
    Category: "Fleet",
    CodeName: "WOStatus"
  });
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [tabActive, setTabActive] = useState("open");
  const [FiltersCodes, setFiltersCodes] = useState(null);
  const [selected, setSelected] = useState([]);
  const client = useApolloClient();

  useEffect(() => {
    if (SystemCodes?.data?.length > 0 && !FiltersCodes) {
      setFiltersCodes({
        Open: SystemCodes.data.filter(
          s => s.CodeId === "ENT" || s.CodeId === "SCH"
        ),
        Completed: SystemCodes.data.find(s => s.CodeId === "COM"),
        Approved: SystemCodes.data.find(s => s.CodeId === "APP"),
        Posted: SystemCodes.data.find(s => s.CodeId === "PST")
      });
    }
  }, [FiltersCodes, SystemCodes]);

  const onChange = name => e => {
    e.preventDefault();
    setTabActive(name);
  };
  const onSelectAll = useCallback((allSelected, recIds) => {
    setSelected(prev => {
      const curr = [...prev];
      for (const RecId of recIds) {
        const idx = curr.findIndex(item => item === RecId);
        if (allSelected) {
          if (idx > -1) {
            curr.splice(idx, 1);
          }
        } else if (idx < 0) {
          curr.push(RecId);
        }
      }
      return curr;
    });
  }, []);

  const onSelect = useCallback(wo => {
    setSelected(prev => {
      const curr = [...prev];
      const idx = curr.findIndex(item => item === wo.RecId);
      if (idx > -1) {
        curr.splice(idx, 1);
      } else {
        curr.push(wo.RecId);
      }

      return curr;
    });
  }, []);

  const approvedWorkOrderColumns = useMemo(
    () =>
      generateApprovedWorkOrderColumns({
        onSelectAll,
        onSelect,
        selected
      }),
    [onSelect, onSelectAll, selected]
  );

  const { data: user } = useCurrentUser();

  const { data: inspectionServiceSystemCode } = useSystemCodes({
    Category: "Fleet",
    CodeName: "WOType",
    CodeDescription: "Inspection Service"
  });

  const postWorkOrder = async (recId, refetch = false) => {
    try {
      if (user) {
        const { data: WorkOrderPartsData } = await client.query({
          query: ALL_WORK_ORDER_PARTS_QUERY,
          variables: { where: { WorkOrderRecIdRef: recId } }
        });

        const parts = WorkOrderPartsData?.WorkOrderParts || [];

        if (
          parts.find(
            part => parseInt(part.DimOH, 10) < parseInt(part.Quantity, 10)
          )
        ) {
          toast.error(
            "Work Order quantity will send On-hand Negative.  Please see supervisor"
          );
        } else {
          setLoading(true);
          const data = {
            PostedDt: new Date(),
            PostedByRecIdRef: parseInt(user.RecId)
          };

          const { data: UpdatedWorkOrderData } = await client.mutate({
            mutation: UPDATE_WORK_ORDER_MUTATION,
            variables: {
              data,
              where: { RecId: parseInt(recId, 10) }
            },
            refetchQueries: refetch
              ? ["ALL_WORK_ORDERS_VIEW_BY_PAGE_QUERY"]
              : []
          });
          if (
            inspectionServiceSystemCode[0].RecId ===
              UpdatedWorkOrderData?.updateWorkOrder?.WOTypeRecIdRef &&
            UpdatedWorkOrderData?.updateWorkOrder?.ItemCatgRecIdRef
          ) {
            const { data: ItemCategoryData } = await client.query({
              query: SINGLE_ITEM_CATEGORY_QUERY,
              variables: {
                where: {
                  RecId: parseInt(
                    UpdatedWorkOrderData.updateWorkOrder.ItemCatgRecIdRef,
                    10
                  )
                }
              }
            });

            const inspectionCdRecIdRef =
              ItemCategoryData?.ItemCategory?.InspectionCdRecIdRef;

            if (
              inspectionCdRecIdRef &&
              UpdatedWorkOrderData?.updateWorkOrder?.VehicleRecIdRef
            ) {
              const {
                data: { VehicleInspections }
              } = await client.query({
                query: ALL_VEHICLE_INSPECTIONS_QUERY,
                variables: {
                  where: {
                    VehicleRecIdRef: parseInt(
                      UpdatedWorkOrderData.updateWorkOrder.VehicleRecIdRef,
                      10
                    ),
                    InspectionTypeRecIdRef: parseInt(inspectionCdRecIdRef, 10)
                  },
                  first: 1,
                  orderByMulti: ["RecId_DESC"]
                },
                fetchPolicy: "network-only"
              });

              const lastInspectionService = VehicleInspections?.[0];
              const lastInspectionDate = new Date(
                lastInspectionService?.InspectionDate
              );
              const today = new Date();
              const daysInterval =
                getDaysDiffBetweenDates(today, lastInspectionDate) || 0;

              const getMechanicFullName = async mechanicRecIdRef => {
                if (mechanicRecIdRef) {
                  const { data: MechanicData } = await client.query({
                    query: SINGLE_EMPLOYEE_QUERY,
                    variables: {
                      where: {
                        RecId: parseInt(mechanicRecIdRef, 10)
                      }
                    }
                  });
                  if (MechanicData?.Employee) {
                    return [
                      MechanicData.Employee.LastName,
                      MechanicData.Employee.FirstName
                    ]
                      .filter(Boolean)
                      .join(", ");
                  }
                }
                return null;
              };

              const mechanicFullName = await getMechanicFullName(
                UpdatedWorkOrderData?.updateWorkOrder?.MechanicRecIdRef
              );

              await client.mutate({
                mutation: CREATE_VEHICLE_INSPECTION_MUTATION,
                variables: {
                  data: {
                    InspectedBy: mechanicFullName,
                    WorkOrderRecIdRef: UpdatedWorkOrderData?.updateWorkOrder
                      ?.RecId
                      ? parseInt(UpdatedWorkOrderData.updateWorkOrder.RecId, 10)
                      : null,
                    MechanicEmployeeRecIdRef: UpdatedWorkOrderData
                      ?.updateWorkOrder?.MechanicRecIdRef
                      ? parseInt(
                          UpdatedWorkOrderData.updateWorkOrder.MechanicRecIdRef,
                          10
                        )
                      : null,
                    VehicleRecIdRef: UpdatedWorkOrderData?.updateWorkOrder
                      ?.VehicleRecIdRef
                      ? parseInt(
                          UpdatedWorkOrderData.updateWorkOrder.VehicleRecIdRef,
                          10
                        )
                      : null,
                    InspectionTypeRecIdRef: parseInt(inspectionCdRecIdRef, 10),
                    OdometerReading: UpdatedWorkOrderData?.updateWorkOrder
                      ?.Odometer
                      ? parseInt(
                          UpdatedWorkOrderData.updateWorkOrder.Odometer,
                          10
                        )
                      : null,
                    MileageInterval:
                      lastInspectionService &&
                      lastInspectionService.OdometerReading !== 0 &&
                      UpdatedWorkOrderData?.updateWorkOrder?.Odometer
                        ? UpdatedWorkOrderData?.updateWorkOrder?.Odometer -
                          lastInspectionService.OdometerReading
                        : null,
                    DaysInterval:
                      daysInterval > 0
                        ? Math.round(daysInterval)
                        : Math.round(daysInterval * -1),
                    InspectionDate: getUTCFormattedDate(today)
                  }
                }
              });
            }
          }
          setLoading(false);
        }
      }
    } catch (error) {
      console.log(error.message);
      toast.error("Error updating Work Order.");
    } finally {
      setLoading(false);
    }
  };

  const handlePostBatch = async () => {
    await runPromisesInSeries(
      selected.map((recId, index) => () =>
        postWorkOrder(recId, selected.length === index + 1)
      )
    );
  };

  useEffect(() => {
    setSelected([]);
  }, [tabActive]);

  return (
    <div>
      <Title size={2}>Work Order Approval and Posting</Title>
      <Tab.Group kind="boxed">
        <Tab active={tabActive === "open"} onClick={onChange("open")}>
          <span>Open</span>
        </Tab>
        <Tab active={tabActive === "completed"} onClick={onChange("completed")}>
          <span>Completed</span>
        </Tab>
        <Tab active={tabActive === "approved"} onClick={onChange("approved")}>
          <span>Approved</span>
        </Tab>
      </Tab.Group>
      {tabActive === "open" && FiltersCodes?.Open && (
        <DataTable
          isPaginated
          columns={WORK_ORDER_COLUMNS(true)}
          dataQuery={ALL_WORK_ORDERS_VIEW_BY_PAGE_QUERY}
          initialSortBy={[
            { id: "WODate", desc: false },
            { id: "WorkOrder", desc: false }
          ]}
          name="VW_WorkOrdersByPage"
          where={{ WOStatusRecIdRef_in: FiltersCodes.Open.map(f => f.RecId) }}
          onRowClick={data =>
            history.push(`/fleet/work-orders/${data.RecId}/main-information`)
          }
        />
      )}
      {tabActive === "completed" && FiltersCodes?.Completed && (
        <DataTable
          isPaginated
          columns={WORK_ORDER_COLUMNS(true)}
          dataQuery={ALL_WORK_ORDERS_VIEW_BY_PAGE_QUERY}
          initialSortBy={[
            { id: "CompletedDate", desc: false },
            { id: "WorkOrder", desc: false }
          ]}
          name="VW_WorkOrdersByPage"
          where={{ WOStatusRecIdRef: FiltersCodes.Completed.RecId }}
          onRowClick={data =>
            history.push(`/fleet/work-orders/${data.RecId}/main-information`)
          }
        />
      )}
      {tabActive === "approved" && FiltersCodes?.Approved && (
        <React.Fragment>
          <header className="page-head">
            <div className="page-head-start" />
            <div className="page-head-end">
              <Field kind="group">
                <Control>
                  <Button
                    color="success"
                    disabled={!selected.length || loading}
                    size="small"
                    state={loading ? "loading" : ""}
                    onClick={handlePostBatch}
                  >
                    <Icon>
                      <FontAwesomeIcon icon="check" />
                    </Icon>
                    <span>Post</span>
                  </Button>
                </Control>
              </Field>
            </div>
          </header>
          <DataTable
            isPaginated
            columns={approvedWorkOrderColumns}
            dataQuery={ALL_WORK_ORDERS_VIEW_BY_PAGE_QUERY}
            initialSortBy={[
              { id: "CompletedDate", desc: false },
              { id: "WorkOrder", desc: false }
            ]}
            name="VW_WorkOrdersByPage"
            where={{ WOStatusRecIdRef: FiltersCodes.Approved.RecId }}
            onRowClick={data =>
              history.push(`/fleet/work-orders/${data.RecId}/main-information`)
            }
          />
        </React.Fragment>
      )}
    </div>
  );
};
WOProcessingPage.propTypes = {};

export default WOProcessingPage;
